/**
 * FAQ Static Data — AUSTRAC Regulatory Compliance FAQs
 * Extracted from FAQ.tsx for maintainability.
 */

export interface FAQItem {
	id: string;
	question: string;
	answer: string;
	tags?: string[];
	relatedIds?: string[];
}

export interface FAQSection {
	id: string;
	title: string;
	description?: string;
	faqs: FAQItem[];
}

export interface HighlightedText {
	type: "normal" | "highlight";
	text: string;
}

/** All AUSTRAC compliance FAQ sections. */
export const FAQ_SECTIONS: FAQSection[] = [
	{
		id: "amlCdd",
		title: "AML/CTF Compliance Essentials",
		description:
			"Learn about Anti-Money Laundering and Counter-Terrorism Financing compliance requirements",
		faqs: [
			{
				id: "faq1",
				question:
					"What is Customer Due Diligence (CDD) and why is it important?",
				answer:
					"Customer Due Diligence is a fundamental requirement under AML/CTF laws. It involves verifying the identity of customers and understanding the nature and purpose of their business relationship. CDD is crucial for identifying and mitigating risks associated with money laundering and terrorist financing.",
				tags: ["CDD", "Customer Verification", "Risk Assessment"],
				relatedIds: ["faq2", "faq9"],
			},
			{
				id: "faq2",
				question: "What are the KYC (Know Your Customer) requirements?",
				answer:
					"KYC requirements mandate that financial institutions and reporting entities verify customer identity, assess risk profiles, and maintain updated customer information. This includes collecting personal identification, understanding the source of funds, and conducting ongoing monitoring throughout the customer relationship.",
				tags: ["KYC", "Customer Identity", "Ongoing Monitoring"],
				relatedIds: ["faq1", "faq3"],
			},
			{
				id: "faq3",
				question: "Is an AML/CTF Compliance Policy mandatory?",
				answer:
					"Yes, all reporting entities under the AML/CTF Act must develop and maintain a written AML/CTF compliance policy. This policy must outline procedures for customer due diligence, reporting of suspicious activities, record keeping, and staff training.",
				tags: ["Policy", "Compliance", "Documentation"],
				relatedIds: ["faq4", "faq11"],
			},
			{
				id: "faq4",
				question: "Do we need to designate an AML/CTF Compliance Officer?",
				answer:
					"Yes, the AML/CTF Act requires reporting entities to have an AML/CTF Compliance Officer responsible for overseeing compliance with the Act. This person should have appropriate authority, resources, and access to senior management.",
				tags: ["Compliance Officer", "Governance", "Authority"],
				relatedIds: ["faq3", "faq11"],
			},
		],
	},
	{
		id: "austriacRequirements",
		title: "AUSTRAC 2026 Regulatory Framework",
		description:
			"Understand the new AUSTRAC regulatory requirements and reporting obligations",
		faqs: [
			{
				id: "faq5",
				question:
					"What are the key changes in the 2026 AUSTRAC Reporting Framework?",
				answer:
					"The 2026 framework introduces enhanced reporting requirements, including real-time reporting capabilities, expanded beneficial ownership transparency, and strengthened cross-border transaction monitoring. These changes aim to improve the detection and prevention of illicit financial flows.",
				tags: ["2026 Framework", "Reporting", "Enhancement"],
				relatedIds: ["faq6", "faq8"],
			},
			{
				id: "faq6",
				question:
					"What is the timeline for implementing the new requirements?",
				answer:
					"Organizations should begin preparing immediately for the 2026 implementation. AUSTRAC recommends completing system upgrades by Q4 2025 to ensure compliance from January 1, 2026. Early implementation is encouraged.",
				tags: ["Timeline", "Implementation", "2026"],
				relatedIds: ["faq5", "faq14"],
			},
			{
				id: "faq7",
				question: "How should we report suspicious activities to AUSTRAC?",
				answer:
					"Suspicious Activity Reports (SARs) must be submitted to AUSTRAC through the dedicated reporting portal. Reports should be filed within 10 business days of detecting the suspicious activity. Both automated systems and manual processes are acceptable, provided they meet accuracy and timeliness standards.",
				tags: ["SAR", "Reporting", "Suspicious Activity"],
				relatedIds: ["faq8", "faq14"],
			},
			{
				id: "faq8",
				question: "What transaction records must we maintain?",
				answer:
					"You must maintain records of all transactions over AUD 10,000, as well as records of suspicious activities. Transaction records should include details such as parties involved, amounts, dates, and purposes. Records must be kept for at least 7 years.",
				tags: ["Record Keeping", "Documentation", "Audit Trail"],
				relatedIds: ["faq7", "faq5"],
			},
		],
	},
	{
		id: "riskAssessment",
		title: "Risk Assessment & Readiness",
		description: "Evaluate your organization's compliance readiness",
		faqs: [
			{
				id: "faq9",
				question: "How do we conduct an AML risk assessment?",
				answer:
					"An AML risk assessment should evaluate inherent risks (customer base, products/services, geographic locations) and residual risks after controls. Document your methodology, involve key stakeholders, and consider both internal and external risk factors. Annual reviews are recommended.",
				tags: ["Risk Assessment", "Methodology", "Internal Controls"],
				relatedIds: ["faq10", "faq1"],
			},
			{
				id: "faq10",
				question: "What should our compliance readiness checklist include?",
				answer:
					"Include system capabilities assessment, staff training completion, policy documentation review, audit trail verification, reporting mechanism testing, and customer data validation. Conduct a dry run of reporting procedures to identify any gaps.",
				tags: ["Checklist", "Readiness", "Testing"],
				relatedIds: ["faq9", "faq11"],
			},
			{
				id: "faq11",
				question: "How often should compliance training be conducted?",
				answer:
					"AUSTRAC requires ongoing AML/CTF training for all staff. Minimum annual training is recommended, with additional training for new staff and when policies change. Training should cover your organization's AML/CTF obligations, procedures, and their specific roles.",
				tags: ["Training", "Staff Development", "Ongoing Education"],
				relatedIds: ["faq10", "faq4"],
			},
			{
				id: "faq12",
				question: "What penalties apply for non-compliance?",
				answer:
					"Penalties for AML/CTF Act breaches can include civil penalties up to AUD 20 million or 10% of adjusted turnover, criminal penalties including imprisonment, and reputational damage. AUSTRAC actively monitors compliance and conducts regular audits.",
				tags: ["Penalties", "Enforcement", "Risk Management"],
				relatedIds: ["faq9", "faq3"],
			},
		],
	},
	{
		id: "implementation",
		title: "Implementation & Support Planning",
		description: "Get guidance on implementing compliance requirements",
		faqs: [
			{
				id: "faq13",
				question:
					"Where can we find resources to support our implementation?",
				answer:
					"AUSTRAC provides comprehensive guidance documents, templates, and case studies on their website. Industry associations and compliance consultants also offer resources. Consider engaging external auditors to validate your implementation.",
				tags: ["Resources", "Support", "Guidance"],
				relatedIds: ["faq14", "faq15"],
			},
			{
				id: "faq14",
				question: "How do we ensure our technology systems are compliant?",
				answer:
					"Audit your current systems for AML/CTF compliance capabilities. Implement solutions that support customer verification, transaction monitoring, report generation, and audit trails. Ensure data security and integrity controls are in place.",
				tags: ["Technology", "Systems", "Integration"],
				relatedIds: ["faq13", "faq6"],
			},
			{
				id: "faq15",
				question:
					"What is the role of external auditors in AML compliance?",
				answer:
					"External auditors verify that your AML/CTF compliance program meets legal requirements and evaluate the effectiveness of your controls. They provide independent assurance and help identify areas for improvement. Annual audits are recommended.",
				tags: ["Auditing", "External Review", "Assurance"],
				relatedIds: ["faq13", "faq10"],
			},
		],
	},
];

/** Highlight search term in text, returning segments. */
export const highlightText = (
	text: string,
	searchTerm: string,
): HighlightedText[] => {
	if (!searchTerm.trim()) return [{ type: "normal", text }];

	const regex = new RegExp(`(${searchTerm.split(" ").join("|")})`, "gi");
	const parts = text.split(regex);

	return parts.map((part) => ({
		type: regex.test(part) ? "highlight" : "normal",
		text: part,
	}));
};
